import type { PasswordStrengthLevel } from "../types/AuthConfig";

export type PasswordRuleKey =
  | "minLength"
  | "lowercase"
  | "uppercase"
  | "number"
  | "specialChar"
  | "noRepeating";

type CharacterRuleKey = "lowercase" | "uppercase" | "number" | "specialChar";

export type PasswordRule = {
  /**
   * i18n key suffix, used as `registration.passwordRules.${key}`
   */
  key: PasswordRuleKey;
  /**
   * Whether the current password satisfies this rule
   */
  passed: boolean;
};

export type PasswordRuleGroup = {
  /**
   * How many rules in this group must pass for the group to be satisfied
   */
  requiredCount: number;
  /**
   * The rules in this group
   */
  rules: PasswordRule[];
  /**
   * Whether the group is satisfied (passed rules >= requiredCount)
   */
  passed: boolean;
};

type UsePasswordValidationParams = {
  /**
   * The password string to validate against the rules
   */
  password: string;
  /**
   * Auth0 password strength level that determines which rules apply.
   * Defaults to "good".
   */
  level?: PasswordStrengthLevel;
};

export type UsePasswordValidationResult = {
  /**
   * Flat list of password rules with their current pass/fail status (backward compat)
   */
  rules: PasswordRule[];
  /**
   * Whether all rule groups are satisfied
   */
  allPassed: boolean;
  /**
   * Per-level minimum password length
   */
  minLength: number;
  /**
   * Grouped rules for UI rendering with "N of M" semantics
   */
  ruleGroups: PasswordRuleGroup[];
};

type LevelConfig = {
  /**
   * Minimum password length for this level
   */
  minLength: number;
  /**
   * Character type rules (lowercase, uppercase, number, specialChar)
   */
  characterRules: CharacterRuleKey[];
  /**
   * How many character rules must pass
   */
  requiredCharacterRules: number;
  /**
   * Additional rules beyond minLength and character types
   */
  additionalRules: PasswordRuleKey[];
};

const LEVEL_CONFIGS: Record<PasswordStrengthLevel, LevelConfig> = {
  none: {
    minLength: 0,
    characterRules: [],
    requiredCharacterRules: 0,
    additionalRules: [],
  },
  low: {
    minLength: 6,
    characterRules: [],
    requiredCharacterRules: 0,
    additionalRules: [],
  },
  fair: {
    minLength: 8,
    characterRules: ["lowercase", "uppercase", "number"],
    requiredCharacterRules: 3,
    additionalRules: [],
  },
  good: {
    minLength: 8,
    characterRules: ["lowercase", "uppercase", "number", "specialChar"],
    requiredCharacterRules: 3,
    additionalRules: [],
  },
  excellent: {
    minLength: 10,
    characterRules: ["lowercase", "uppercase", "number", "specialChar"],
    requiredCharacterRules: 3,
    additionalRules: ["noRepeating"],
  },
};

const CHAR_VALIDATORS: Record<CharacterRuleKey, (password: string) => boolean> =
  {
    lowercase: (pw) => /[a-z]/.test(pw),
    uppercase: (pw) => /[A-Z]/.test(pw),
    number: (pw) => /\d/.test(pw),
    specialChar: (pw) => /[^a-zA-Z0-9]/.test(pw),
  };

const ADDITIONAL_VALIDATORS: Record<string, (password: string) => boolean> = {
  noRepeating: (pw) => !/(.)\1{2,}/.test(pw),
};

const DEFAULT_LEVEL: PasswordStrengthLevel = "good";

/**
 * Pure validation function that checks a password against the rules
 * defined by the given Auth0 strength level.
 * Returns grouped rules with "N of M" semantics matching Auth0's password-sheriff.
 */
export const validatePassword = ({
  password,
  level = DEFAULT_LEVEL,
}: UsePasswordValidationParams): UsePasswordValidationResult => {
  const config = LEVEL_CONFIGS[level] ?? LEVEL_CONFIGS[DEFAULT_LEVEL];
  const ruleGroups: PasswordRuleGroup[] = [];
  const allRules: PasswordRule[] = [];

  if (config.minLength > 0) {
    const passed = password.length >= config.minLength;
    const rule: PasswordRule = { key: "minLength", passed };
    allRules.push(rule);
    ruleGroups.push({ requiredCount: 1, rules: [rule], passed });
  }

  if (config.characterRules.length > 0) {
    const charRules = config.characterRules.map((key) => ({
      key: key as PasswordRuleKey,
      passed: CHAR_VALIDATORS[key](password),
    }));
    const passedCount = charRules.filter((r) => r.passed).length;

    allRules.push(...charRules);
    ruleGroups.push({
      requiredCount: config.requiredCharacterRules,
      rules: charRules,
      passed: passedCount >= config.requiredCharacterRules,
    });
  }

  for (const key of config.additionalRules) {
    const validator = ADDITIONAL_VALIDATORS[key];
    if (validator) {
      const passed = validator(password);
      const rule: PasswordRule = { key, passed };
      allRules.push(rule);
      ruleGroups.push({ requiredCount: 1, rules: [rule], passed });
    }
  }

  const allPassed =
    ruleGroups.length === 0 || ruleGroups.every((g) => g.passed);

  return {
    rules: allRules,
    allPassed,
    minLength: config.minLength,
    ruleGroups,
  };
};

/**
 * Hook that validates a password against Auth0 strength level rules.
 * Returns the list of applicable rules with pass/fail status,
 * grouped rules for UI rendering, and whether all rules are satisfied.
 */
export const usePasswordValidation = ({
  password,
  level = DEFAULT_LEVEL,
}: UsePasswordValidationParams): UsePasswordValidationResult => {
  return validatePassword({ password, level });
};
